import { Injectable } from '@angular/core';
import { Barcode, BarcodeFormat, BarcodeScanner, StartScanOptions } from '@capacitor-mlkit/barcode-scanning';
import { TrackerService } from '@services/tracker.service';
import { Gs1Encoder } from 'src/app/shared/gs1-encoder/gs1-encoder';

/**
 *
 */
export type ExtendedBarcode = Barcode & {
  isGs1Barcode?: boolean;
  /**
   * GS1 data items
   * @example
   * [
   *   {
   *     ai: '10',
   *     dataTitle: 'Batch or Lot Number',
   *     data: 'ABC123',
   *     unit: '',
   *     raw: '10 ABC123'
   *   },
   *   {
   *     ai: '21',
   *     dataTitle: 'Serial Number',
   *     data: '1234567890',
   *     unit: '',
   *     raw: '21 1234567890'
   *   }
   * ]
   */
  gs1Data?: {
    /**
     * the application identifier eg. 10 for Batch/Lot Number
     * @example '10'
     */
    ai: string;
    /**
     * the data title eg. Batch or Lot Number
     * @example 'Batch or Lot Number'
     */
    dataTitle: string;
    /**
     * the data value eg. ABC123
     * @example 'ABC123'
     */
    data: string;
    /**
     * the unit of measure
     */
    unit: string;
    /**
     * the raw data string
     * @example '10 ABC123'
     */
    raw: string;
  }[];
};

@Injectable()
export class BarcodeScanningService {
  constructor(
    private trackerService: TrackerService,
    private gs1Encoder: Gs1Encoder
  ) {}

  /**
   * Checks if torch is available for the camera
   */
  public async isTorchAvailable(): Promise<boolean> {
    const result = await BarcodeScanner.isTorchAvailable();
    return result.available;
  }

  /**
   * Toggles the flashlight on/off
   */
  public async toggleTorch(): Promise<void> {
    await BarcodeScanner.toggleTorch();
  }

  /**
   * Sets the zoom ratio for the camera
   */
  public async setZoomRatio(zoomRatio: number): Promise<void> {
    this.trackerService.trackBarcodeSetZoomLevel(zoomRatio);
    await BarcodeScanner.setZoomRatio({ zoomRatio });
  }

  /**
   * Gets the minimum zoom ratio for the camera
   */
  public async getMinZoomRatio(): Promise<number> {
    const result = await BarcodeScanner.getMinZoomRatio();
    return result.zoomRatio;
  }

  /**
   * Gets the maximum zoom ratio for the camera, capped at 5
   */
  public async getMaxZoomRatio(): Promise<number> {
    const result = await BarcodeScanner.getMaxZoomRatio();
    return result.zoomRatio > 5 ? 5 : result.zoomRatio;
  }

  /**
   * Starts barcode scanning with given options
   */
  public async startScan(options: StartScanOptions): Promise<void> {
    this.trackerService.trackBarcodeScannerOpened();
    await BarcodeScanner.startScan(options);
  }

  /**
   * Stops barcode scanning
   */
  public async stopScan(): Promise<void> {
    await BarcodeScanner.stopScan();
  }

  /**
   * Adds a listener for barcode scanning events
   */
  public async addBarcodeListener(callback: (event: { barcodes: Barcode[] }) => void): Promise<any> {
    return await BarcodeScanner.addListener('barcodesScanned', callback);
  }

  /**
   * Filters barcodes to find those that are inside the square element
   */
  public filterBarcodesInSquare(barcodes: Barcode[], squareCornerPoints: number[][]): ExtendedBarcode[] {
    return barcodes.filter(barcode => {
      const barcodeCornerPoints = barcode.cornerPoints;
      if (squareCornerPoints && barcodeCornerPoints) {
        return this.isInsideSquareElement(barcodeCornerPoints, squareCornerPoints);
      }
    }) as ExtendedBarcode[];
  }

  /**
   * Processes a barcode to determine its display text
   */
  public processBarcodeText(barcode: ExtendedBarcode, parseGS1: boolean): string | undefined {
    const isGS1Compatible =
      parseGS1 && (barcode.format === BarcodeFormat.DataMatrix || barcode.format === BarcodeFormat.Code128);

    if (!isGS1Compatible) {
      return barcode.displayValue;
    }

    try {
      return this.processGS1Barcode(barcode);
    } catch (_error) {
      return barcode.displayValue;
    }
  }

  /**
   * Processes a GS1 format barcode
   */
  private processGS1Barcode(barcode: ExtendedBarcode): string {
    const gs1Data = this.gs1Encoder.getGs1Data(barcode);

    if (!gs1Data?.parsedCodeItems?.length) {
      return barcode.displayValue;
    }

    gs1Data.parsedCodeItems = gs1Data.parsedCodeItems.map(item =>
      (item.data as unknown) instanceof Date ? { ...item, data: item.raw } : item
    );

    const hasRelevantAI = gs1Data.parsedCodeItems.some(item => item.ai === '21' || item.ai === '10');

    if (hasRelevantAI) {
      barcode.isGs1Barcode = true;
      barcode.gs1Data = gs1Data.parsedCodeItems;
      return gs1Data.parsedCodeItems.map(item => `${item.dataTitle}(${item.ai}):${item.data}`).join('\n\n');
    }

    return barcode.displayValue;
  }

  /**
   * Checks if barcode is inside the square element
   */
  public isInsideSquareElement(
    barcodeCornerPoints: [[number, number], [number, number], [number, number], [number, number]],
    squareCornerPoints: number[][]
  ): boolean {
    return !this.isOutsideSquareElement(barcodeCornerPoints, squareCornerPoints);
  }

  /**
   * Checks if barcode is outside the square element
   */
  private isOutsideSquareElement(
    barcodeCornerPoints: [[number, number], [number, number], [number, number], [number, number]],
    squareCornerPoints: number[][]
  ): boolean {
    if (
      squareCornerPoints[0][0] > barcodeCornerPoints[0][0] ||
      squareCornerPoints[0][1] > barcodeCornerPoints[0][1] ||
      squareCornerPoints[1][0] < barcodeCornerPoints[1][0] ||
      squareCornerPoints[1][1] > barcodeCornerPoints[1][1] ||
      squareCornerPoints[2][0] < barcodeCornerPoints[2][0] ||
      squareCornerPoints[2][1] < barcodeCornerPoints[2][1] ||
      squareCornerPoints[3][0] > barcodeCornerPoints[3][0] ||
      squareCornerPoints[3][1] < barcodeCornerPoints[3][1]
    ) {
      return true;
    }
    return false;
  }

  /**
   * Track that a barcode has been received
   */
  public trackBarcodeReceived(): void {
    this.trackerService.trackBarcodeReceived();
  }
}
